import { createContext } from "react";
import {
	CUSTOM_FIELD_TYPE,
	DISPLAY_PREFERENCE_TYPE,
	DISPLAY_PREFERENCE_SECTION_DISPLAY,
} from "~/types/__generated/gql/graphql";

export enum DATA_DISPLAY_CHANGE_TYPE {
	INFO = "info",
	ADDRESSES = "addresses",
	CONTACTS = "contacts",
	SERVICE = "service",
	SECTIONS = "sections",
}

export interface ISectionField {
	sortingIndex: number;
	id: string;
	isVisible: boolean;
}

export interface ISectionRow {
	sortingIndex: number;
	fields: ISectionField[];
}

export interface ISection {
	name: string;
	display: DISPLAY_PREFERENCE_SECTION_DISPLAY;
	sortingIndex: number;
	rows: ISectionRow[];
}

export const getFileCustomFieldDisplayId = (customFieldId: string) =>
	`basic-info-custom-fields-${customFieldId}`;

export interface DataDisplayList {
	parentFieldName?: string;
	fieldName: string;
	fieldLabel: string;
	fieldType: CUSTOM_FIELD_TYPE;
	sortingIndex: number;
	isCustomField: boolean;
	isPinned: boolean;
	isVisible?: boolean;
	width?: number;
}

export interface DataDisplayFormDetails {
	sortingIndex: number;
	fields: DataDisplayFormDetail[];
}

export interface DataDisplayFormDetail {
	parentFieldName?: string;
	fieldName: string;
	fieldLabel: string;
	fieldType: CUSTOM_FIELD_TYPE;
	sortingIndex: number;
	isCustomField: boolean;
	isPinned: boolean;
}

export interface DataDisplayForm {
	info: DataDisplayFormDetails[];
	addresses: DataDisplayFormDetails[];
	contacts: DataDisplayFormDetails[];
	service: DataDisplayFormDetails[];
}

export interface DataDisplayAgent {
	isFormModelOpen: boolean;
	isListModelOpen: boolean;
	list: DataDisplayList[];
	form: DataDisplayForm;
}

export interface DataDisplayClient {
	isFormModelOpen: boolean;
	isListModelOpen: boolean;
	list: DataDisplayList[];
	form: DataDisplayForm;
}

export interface DataDisplaySupplier {
	isFormModelOpen: boolean;
	isListModelOpen: boolean;
	list: DataDisplayList[];
	form: Omit<DataDisplayForm, "service">;
}

export interface DataDisplayReceiver {
	isFormModelOpen: boolean;
	isListModelOpen: boolean;
	list: DataDisplayList[];
	form: Omit<DataDisplayForm, "service">;
}

export interface DataDisplayProduct {
	isListModelOpen: boolean;
	list: DataDisplayList[];
}

export interface DataDisplayServiceAgent {
	isListModelOpen: boolean;
	list: DataDisplayList[];
}

export interface DataDisplayServiceClient {
	isListModelOpen: boolean;
	list: DataDisplayList[];
}

export interface DataDisplayAccountTransaction {
	isFormModelOpen: boolean;
	isListModelOpen: boolean;
	list: DataDisplayList[];
	form: {
		sections: ISection[];
	};
}

export interface DataDisplayPaymentAttachment {
	isFormModelOpen: boolean;
	isListModelOpen: boolean;
	list: DataDisplayList[];
	form: {
		sections: ISection[];
	};
}

export interface DataDisplayFile {
	isFormModelOpen: boolean;
	isListModelOpen: boolean;
	list: DataDisplayList[];
	form: {
		sections: ISection[];
	};
}

export interface DataDisplayState {
	agent: DataDisplayAgent;
	client: DataDisplayClient;
	supplier: DataDisplaySupplier;
	receiver: DataDisplayReceiver;
	product: DataDisplayProduct;
	serviceAgent: DataDisplayServiceAgent;
	serviceClient: DataDisplayServiceClient;
	accountTransaction: DataDisplayAccountTransaction;
	paymentAttachment: DataDisplayPaymentAttachment;
	file: DataDisplayFile;
}

const fileFormSections: ISection[] = [
	{
		name: "General Information",
		display: DISPLAY_PREFERENCE_SECTION_DISPLAY.GRID,
		sortingIndex: 0,
		rows: [
			{
				sortingIndex: 0,
				fields: [
					{ sortingIndex: 0, id: "general-info-created-by", isVisible: true },
					{ sortingIndex: 1, id: "general-info-created-at", isVisible: true },
					{ sortingIndex: 2, id: "general-info-last-updated", isVisible: true },
				],
			},
		],
	},
	{
		name: "Basic Information",
		display: DISPLAY_PREFERENCE_SECTION_DISPLAY.GRID,
		sortingIndex: 1,
		rows: [
			{
				sortingIndex: 0,
				fields: [
					{ sortingIndex: 0, id: "basic-info-client", isVisible: true },
					{ sortingIndex: 1, id: "basic-info-agents", isVisible: true },
					{ sortingIndex: 2, id: "basic-info-assignees", isVisible: true },
				],
			},
			{
				sortingIndex: 1,
				fields: [
					{ sortingIndex: 3, id: "basic-info-file-number", isVisible: true },
				],
			},
			{
				sortingIndex: 2,
				fields: [
					{
						sortingIndex: 4,
						id: "basic-info-manage-custom-fields",
						isVisible: true,
					},
				],
			},
		],
	},
	{
		name: "Invoice Information",
		display: DISPLAY_PREFERENCE_SECTION_DISPLAY.GRID,
		sortingIndex: 2,
		rows: [
			{
				sortingIndex: 0,
				fields: [
					{
						sortingIndex: 0,
						id: "invoice-info-invoice-number",
						isVisible: true,
					},
					{ sortingIndex: 1, id: "invoice-info-issue-date", isVisible: true },
					{ sortingIndex: 2, id: "invoice-info-due-date", isVisible: true },
				],
			},
		],
	},
	{
		name: "Product Information",
		display: DISPLAY_PREFERENCE_SECTION_DISPLAY.GRID,
		sortingIndex: 3,
		rows: [
			{
				sortingIndex: 0,
				fields: [
					{ sortingIndex: 0, id: "product-info-number", isVisible: true },
					{ sortingIndex: 1, id: "product-info-quantity", isVisible: true },
					{ sortingIndex: 2, id: "product-info-description", isVisible: true },
					{ sortingIndex: 3, id: "product-info-total", isVisible: true },
					{ sortingIndex: 4, id: "product-info-actions", isVisible: true },
				],
			},
		],
	},
	{
		name: "Payment Records",
		display: DISPLAY_PREFERENCE_SECTION_DISPLAY.GRID,
		sortingIndex: 4,
		rows: [
			{
				sortingIndex: 0,
				fields: [
					{ sortingIndex: 0, id: "payment-record-created", isVisible: true },
					{
						sortingIndex: 1,
						id: "payment-record-bank-received",
						isVisible: true,
					},
					{
						sortingIndex: 2,
						id: "payment-record-payment-type",
						isVisible: true,
					},
					{
						sortingIndex: 3,
						id: "payment-record-attachment-number",
						isVisible: true,
					},
					{
						sortingIndex: 4,
						id: "payment-record-payment-amount",
						isVisible: true,
					},
					{
						sortingIndex: 5,
						id: "payment-record-payment-currency",
						isVisible: true,
					},
					{
						sortingIndex: 6,
						id: "payment-record-exchange-rate",
						isVisible: true,
					},
					{
						sortingIndex: 7,
						id: "payment-record-order-currency",
						isVisible: true,
					},
					{
						sortingIndex: 8,
						id: "payment-record-order-amount",
						isVisible: true,
					},
					{
						sortingIndex: 9,
						id: "payment-record-payment-stage",
						isVisible: true,
					},
					{
						sortingIndex: 10,
						id: "payment-record-voided-amount",
						isVisible: true,
					},
				],
			},
		],
	},
	{
		name: "Payment Attachments",
		display: DISPLAY_PREFERENCE_SECTION_DISPLAY.GRID,
		sortingIndex: 5,
		rows: [
			{
				sortingIndex: 0,
				fields: [
					{ sortingIndex: 0, id: "payment-attachment-number", isVisible: true },
					{ sortingIndex: 1, id: "payment-attachment-amount", isVisible: true },
					{
						sortingIndex: 2,
						id: "payment-attachment-uploaded",
						isVisible: true,
					},
					{
						sortingIndex: 3,
						id: "payment-attachment-attachment",
						isVisible: true,
					},
					{
						sortingIndex: 4,
						id: "payment-attachment-payment-amount",
						isVisible: true,
					},
					{
						sortingIndex: 5,
						id: "payment-attachment-payment-currency",
						isVisible: true,
					},
					{
						sortingIndex: 6,
						id: "payment-attachment-exchange-rate",
						isVisible: true,
					},
					{
						sortingIndex: 7,
						id: "payment-attachment-order-currency",
						isVisible: true,
					},
					{
						sortingIndex: 8,
						id: "payment-attachment-order-amount",
						isVisible: true,
					},
					{
						sortingIndex: 9,
						id: "payment-attachment-received-status",
						isVisible: true,
					},
					{
						sortingIndex: 10,
						id: "payment-attachment-payment-stage",
						isVisible: true,
					},
					{
						sortingIndex: 11,
						id: "payment-attachment-status",
						isVisible: true,
					},
					{ sortingIndex: 12, id: "payment-attachment-void", isVisible: true },
					{
						sortingIndex: 13,
						id: "payment-attachment-voided-amount",
						isVisible: true,
					},
					{
						sortingIndex: 14,
						id: "payment-attachment-payment-action",
						isVisible: true,
					},
					{
						sortingIndex: 15,
						id: "payment-attachment-remove-action",
						isVisible: true,
					},
				],
			},
		],
	},
];

export const initialState: DataDisplayState = {
	agent: {
		isFormModelOpen: false,
		isListModelOpen: false,
		list: [],
		form: {
			info: [
				{
					sortingIndex: 0,
					fields: [
						{
							fieldName: "name",
							fieldLabel: "Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							fieldName: "companyIssuedId",
							fieldLabel: "Company Issued ID",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			addresses: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "addresses",
							fieldName: "title",
							fieldLabel: "Title",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "country",
							fieldLabel: "Country",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "province",
							fieldLabel: "Province",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "city",
							fieldLabel: "City",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 3,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
				{
					sortingIndex: 1,
					fields: [
						{
							parentFieldName: "addresses",
							fieldName: "area",
							fieldLabel: "Area",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 4,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "street",
							fieldLabel: "Street",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 5,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "building",
							fieldLabel: "Building",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 6,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "unit",
							fieldLabel: "Unit",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 7,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
				{
					sortingIndex: 2,
					fields: [
						{
							parentFieldName: "addresses",
							fieldName: "floor",
							fieldLabel: "Floor",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 8,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			contacts: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "contacts",
							fieldName: "gender",
							fieldLabel: "Gender",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "email",
							fieldLabel: "Email",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "firstName",
							fieldLabel: "First Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "lastName",
							fieldLabel: "Last Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 3,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
				{
					sortingIndex: 1,
					fields: [
						{
							parentFieldName: "contacts",
							fieldName: "telephone",
							fieldLabel: "Telephone",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 4,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "mobile",
							fieldLabel: "Mobile",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 5,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "position",
							fieldLabel: "Position",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 6,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			service: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "hiddenProductsConfigs",
							fieldName: "categories",
							fieldLabel: "Categories",
							fieldType: CUSTOM_FIELD_TYPE.SELECT_MULTI,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "hiddenProductsConfigs",
							fieldName: "products",
							fieldLabel: "Product",
							fieldType: CUSTOM_FIELD_TYPE.SELECT_MULTI,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "hiddenProductsConfigs",
							fieldName: "types",
							fieldLabel: "Types",
							fieldType: CUSTOM_FIELD_TYPE.SELECT_MULTI,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
		},
	},
	client: {
		isFormModelOpen: false,
		isListModelOpen: false,
		list: [],
		form: {
			info: [
				{
					sortingIndex: 0,
					fields: [
						{
							fieldName: "name",
							fieldLabel: "Product Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							fieldName: "companyIssuedId",
							fieldLabel: "Company Issued ID",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			addresses: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "addresses",
							fieldName: "title",
							fieldLabel: "Title",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "country",
							fieldLabel: "Country",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "province",
							fieldLabel: "Province",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "city",
							fieldLabel: "City",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 3,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
				{
					sortingIndex: 1,
					fields: [
						{
							parentFieldName: "addresses",
							fieldName: "area",
							fieldLabel: "Area",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 4,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "street",
							fieldLabel: "Street",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 5,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "building",
							fieldLabel: "Building",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 6,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "unit",
							fieldLabel: "Unit",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 7,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
				{
					sortingIndex: 2,
					fields: [
						{
							parentFieldName: "addresses",
							fieldName: "floor",
							fieldLabel: "Floor",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 8,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			contacts: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "contacts",
							fieldName: "gender",
							fieldLabel: "Gender",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "email",
							fieldLabel: "Email",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "firstName",
							fieldLabel: "First Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "lastName",
							fieldLabel: "Last Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 3,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
				{
					sortingIndex: 1,
					fields: [
						{
							parentFieldName: "contacts",
							fieldName: "telephone",
							fieldLabel: "Telephone",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 4,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "mobile",
							fieldLabel: "Mobile",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 5,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "position",
							fieldLabel: "Position",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 6,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			service: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "hiddenProductsConfigs",
							fieldName: "categories",
							fieldLabel: "Categories",
							fieldType: CUSTOM_FIELD_TYPE.SELECT_MULTI,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "hiddenProductsConfigs",
							fieldName: "products",
							fieldLabel: "Product",
							fieldType: CUSTOM_FIELD_TYPE.SELECT_MULTI,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "hiddenProductsConfigs",
							fieldName: "types",
							fieldLabel: "Types",
							fieldType: CUSTOM_FIELD_TYPE.SELECT_MULTI,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
		},
	},
	supplier: {
		isFormModelOpen: false,
		isListModelOpen: false,
		list: [],
		form: {
			info: [
				{
					sortingIndex: 0,
					fields: [
						{
							fieldName: "id",
							fieldLabel: "Supplier ID",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							fieldName: "name",
							fieldLabel: "Supplier Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			addresses: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "addresses",
							fieldName: "country",
							fieldLabel: "Country",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "province",
							fieldLabel: "Province",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "city",
							fieldLabel: "City",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
				{
					sortingIndex: 1,
					fields: [
						{
							parentFieldName: "addresses",
							fieldName: "area",
							fieldLabel: "Area",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 3,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "street",
							fieldLabel: "Street",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 4,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "building",
							fieldLabel: "Building",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 5,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			contacts: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "contacts",
							fieldName: "email",
							fieldLabel: "Contact Email",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "name",
							fieldLabel: "Contact Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "number",
							fieldLabel: "Phone Number",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
		},
	},
	receiver: {
		isFormModelOpen: false,
		isListModelOpen: false,
		list: [],
		form: {
			info: [
				{
					sortingIndex: 0,
					fields: [
						{
							fieldName: "id",
							fieldLabel: "Receiver ID",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							fieldName: "name",
							fieldLabel: "Receiver Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			addresses: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "addresses",
							fieldName: "country",
							fieldLabel: "Country",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "province",
							fieldLabel: "Province",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "city",
							fieldLabel: "City",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
				{
					sortingIndex: 1,
					fields: [
						{
							parentFieldName: "addresses",
							fieldName: "area",
							fieldLabel: "Area",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 3,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "street",
							fieldLabel: "Street",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 4,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "building",
							fieldLabel: "Building",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 5,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			contacts: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "contacts",
							fieldName: "email",
							fieldLabel: "Contact Email",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "name",
							fieldLabel: "Contact Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "number",
							fieldLabel: "Phone Number",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
		},
	},
	product: {
		isListModelOpen: false,
		list: [],
	},
	serviceAgent: {
		isListModelOpen: false,
		list: [
			{
				fieldName: "productId",
				fieldLabel: "Product ID",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 0,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "name",
				fieldLabel: "Name",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 1,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "price",
				fieldLabel: "Price",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 2,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "category",
				fieldLabel: "Category",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 3,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "type",
				fieldLabel: "Type",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 4,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "optionGroups",
				fieldLabel: "Option Groups",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 5,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "options",
				fieldLabel: "Options",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 6,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
		],
	},
	serviceClient: {
		isListModelOpen: false,
		list: [
			{
				fieldName: "productId",
				fieldLabel: "Product ID",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 0,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "name",
				fieldLabel: "Name",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 1,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "price",
				fieldLabel: "Price",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 2,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "category",
				fieldLabel: "Category",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 3,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "type",
				fieldLabel: "Type",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 4,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "optionGroups",
				fieldLabel: "Option Groups",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 5,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "options",
				fieldLabel: "Options",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 6,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
		],
	},
	accountTransaction: {
		isFormModelOpen: false,
		isListModelOpen: false,
		list: [
			{
				fieldName: "transactionNumber",
				fieldLabel: "Transaction #",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 0,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
				width: 200,
			},
			{
				fieldName: "createdBy",
				fieldLabel: "Created By",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 1,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "createdAt",
				fieldLabel: "Created At",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 2,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "accountName",
				fieldLabel: "Account Name",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 3,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "accountId",
				fieldLabel: "Account ID",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 4,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "agentName",
				fieldLabel: "Agent Name",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 5,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "clientName",
				fieldLabel: "Client Name",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 6,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "orderNumber",
				fieldLabel: "Order Number",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 7,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "paymentType",
				fieldLabel: "Payment Type",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 8,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "paymentAmount",
				fieldLabel: "Payment Amount",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 9,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "bankPaymentReceivedDate",
				fieldLabel: "Bank Payment Received Date",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 10,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
				width: 280,
			},
			{
				fieldName: "paymentAttachment",
				fieldLabel: "Payment Attachment",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 11,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "paymentAttachmentAmount",
				fieldLabel: "Payment Attachment Amount",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 12,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "paymentAttachmentReferenceNumber",
				fieldLabel: "Payment Attachment Number",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 13,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
				width: 280,
			},
			{
				fieldName: "paymentAttachmentStage",
				fieldLabel: "Payment Attachment Stage",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 14,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "comments",
				fieldLabel: "Comments",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 15,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "paymentReferenceNumber",
				fieldLabel: "Payment Reference Number",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 16,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
		],
		form: {
			sections: [
				{
					name: "Basic Info",
					display: DISPLAY_PREFERENCE_SECTION_DISPLAY.GRID,
					sortingIndex: 0,
					rows: [
						{
							sortingIndex: 0,
							fields: [
								{
									sortingIndex: 0,
									id: "account-transaction-prefix",
									isVisible: true,
								},
								{
									sortingIndex: 1,
									id: "select-account-name",
									isVisible: true,
								},
								{
									sortingIndex: 2,
									id: "select-account-id",
									isVisible: true,
								},
							],
						},
					],
				},
				{
					name: "Order Info",
					display: DISPLAY_PREFERENCE_SECTION_DISPLAY.GRID,
					sortingIndex: 1,
					rows: [
						{
							sortingIndex: 0,
							fields: [
								{
									sortingIndex: 0,
									id: "select-unpaid-files",
									isVisible: true,
								},
							],
						},
						{
							sortingIndex: 1,
							fields: [
								{
									sortingIndex: 0,
									id: "select-monthly-statements",
									isVisible: true,
								},
								{
									sortingIndex: 1,
									id: "select-payee",
									isVisible: true,
								},
								{
									sortingIndex: 2,
									id: "select-agents",
									isVisible: true,
								},
								{
									sortingIndex: 3,
									id: "select-clients",
									isVisible: true,
								},
							],
						},
						{
							sortingIndex: 2,
							fields: [
								{
									sortingIndex: 0,
									id: "select-unpaid-invoices",
									isVisible: true,
								},
							],
						},
					],
				},
				{
					name: "Payment Information",
					display: DISPLAY_PREFERENCE_SECTION_DISPLAY.GRID,
					sortingIndex: 2,
					rows: [
						{
							sortingIndex: 0,
							fields: [
								{
									sortingIndex: 0,
									id: "select-payment-type",
									isVisible: true,
								},
								{
									sortingIndex: 1,
									id: "select-payment-currency",
									isVisible: true,
								},
								{
									sortingIndex: 2,
									id: "payment-amount",
									isVisible: true,
								},
								{
									sortingIndex: 3,
									id: "select-payment-stage",
									isVisible: true,
								},
							],
						},
						{
							sortingIndex: 1,
							fields: [
								{
									sortingIndex: 0,
									id: "bank-received-date",
									isVisible: true,
								},
								{
									sortingIndex: 1,
									id: "payment-reference-number",
									isVisible: true,
								},
							],
						},
						{
							sortingIndex: 2,
							fields: [
								{
									sortingIndex: 0,
									id: "comments",
									isVisible: true,
								},
							],
						},
					],
				},
				{
					name: "Payment Attachments",
					display: DISPLAY_PREFERENCE_SECTION_DISPLAY.STACK,
					sortingIndex: 3,
					rows: [
						{
							sortingIndex: 0,
							fields: [
								{
									sortingIndex: 0,
									id: "pa-uploaded",
									isVisible: true,
								},
								{
									sortingIndex: 1,
									id: "pa-received-amount",
									isVisible: true,
								},
								{
									sortingIndex: 2,
									id: "pa-select-received-currency",
									isVisible: true,
								},
								{
									sortingIndex: 3,
									id: "pa-received-date",
									isVisible: true,
								},
								{
									sortingIndex: 4,
									id: "pa-select-payment-stage",
									isVisible: true,
								},
								{
									sortingIndex: 5,
									id: "pa-select-payment-purpose",
									isVisible: true,
								},
								{
									sortingIndex: 6,
									id: "pa-select-received-status",
									isVisible: true,
								},
								{
									sortingIndex: 7,
									id: "pa-select-attachment",
									isVisible: true,
								},
							],
						},
					],
				},
				{
					name: "Voided Payment Attachments",
					display: DISPLAY_PREFERENCE_SECTION_DISPLAY.STACK,
					sortingIndex: 4,
					rows: [
						{
							sortingIndex: 0,
							fields: [
								{
									sortingIndex: 0,
									id: "vpa-reference-number",
									isVisible: true,
								},
								{
									sortingIndex: 1,
									id: "vpa-uploaded",
									isVisible: true,
								},
								{
									sortingIndex: 2,
									id: "vpa-received-amount",
									isVisible: true,
								},
								{
									sortingIndex: 3,
									id: "vpa-select-received-currency",
									isVisible: true,
								},
								{
									sortingIndex: 4,
									id: "vpa-received-date",
									isVisible: true,
								},
								{
									sortingIndex: 5,
									id: "vpa-select-payment-stage",
									isVisible: true,
								},
								{
									sortingIndex: 6,
									id: "vpa-select-payment-purpose",
									isVisible: true,
								},
								{
									sortingIndex: 7,
									id: "vpa-select-received-status",
									isVisible: true,
								},
								{
									sortingIndex: 8,
									id: "vpa-select-attachment",
									isVisible: true,
								},
							],
						},
					],
				},
				{
					name: "Order Details",
					display: DISPLAY_PREFERENCE_SECTION_DISPLAY.TABLE,
					sortingIndex: 5,
					rows: [
						{
							sortingIndex: 0,
							fields: [
								{
									sortingIndex: 0,
									id: "od-order-number",
									isVisible: true,
								},
								{
									sortingIndex: 1,
									id: "od-agent",
									isVisible: true,
								},
								{
									sortingIndex: 2,
									id: "od-client",
									isVisible: true,
								},
								{
									sortingIndex: 3,
									id: "od-process",
									isVisible: true,
								},
								{
									sortingIndex: 4,
									id: "od-payment-stage",
									isVisible: true,
								},
								{
									sortingIndex: 5,
									id: "od-order-amount",
									isVisible: true,
								},
								{
									sortingIndex: 6,
									id: "od-order-currency",
									isVisible: true,
								},
								{
									sortingIndex: 7,
									id: "od-payment-amount",
									isVisible: true,
								},
								{
									sortingIndex: 8,
									id: "od-payment-currency",
									isVisible: true,
								},
								{
									sortingIndex: 9,
									id: "od-payment-to-order-xr",
									isVisible: true,
								},
								{
									sortingIndex: 10,
									id: "od-balance-amount",
									isVisible: true,
								},
								{
									sortingIndex: 11,
									id: "od-balance-currency",
									isVisible: true,
								},
								{
									sortingIndex: 12,
									id: "od-payment-status",
									isVisible: true,
								},
								{
									sortingIndex: 13,
									id: "od-action",
									isVisible: true,
								},
							],
						},
					],
				},
			],
		},
	},
	paymentAttachment: {
		isFormModelOpen: false,
		isListModelOpen: false,
		list: [
			{
				fieldName: "createdAt",
				fieldLabel: "Created At",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 0,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "paymentAttachmentNumber",
				fieldLabel: "Payment Attachment #",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 1,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
				width: 200,
			},
			{
				fieldName: "transactionNumber",
				fieldLabel: "Transaction #",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 2,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
				width: 200,
			},
			{
				fieldName: "createdBy",
				fieldLabel: "Created By",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 3,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "agents",
				fieldLabel: "Agents",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 4,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "clients",
				fieldLabel: "Clients",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 5,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "orderNumber",
				fieldLabel: "Order #",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 6,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "currency",
				fieldLabel: "Currency",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 7,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "paymentAttachmentAmount",
				fieldLabel: "Payment Attachment Amount",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 8,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
				width: 280,
			},
			{
				fieldName: "paymentAttachmentReceivedDate",
				fieldLabel: "Payment Attachment Received Date",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 9,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
				width: 280,
			},
			{
				fieldName: "paymentPurpose",
				fieldLabel: "Payment Purpose",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 10,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "receivedStatus",
				fieldLabel: "Received Status",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 11,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "allocationStatus",
				fieldLabel: "Allocation Status",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 12,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "remarks",
				fieldLabel: "Remarks",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 13,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
			{
				fieldName: "action",
				fieldLabel: "Action",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 14,
				isCustomField: false,
				isPinned: false,
				isVisible: true,
			},
		],
		form: {
			sections: [
				{
					name: "Payment Attachment",
					display: DISPLAY_PREFERENCE_SECTION_DISPLAY.GRID,
					sortingIndex: 0,
					rows: [
						{
							sortingIndex: 0,
							fields: [
								{
									sortingIndex: 0,
									id: "pa-upload",
									isVisible: true,
								},
							],
						},
						{
							sortingIndex: 1,
							fields: [
								{
									sortingIndex: 0,
									id: "pa-select-files",
									isVisible: true,
								},
							],
						},
						{
							sortingIndex: 2,
							fields: [
								{
									sortingIndex: 0,
									id: "pa-select-agents",
									isVisible: true,
								},
							],
						},
						{
							sortingIndex: 3,
							fields: [
								{
									sortingIndex: 0,
									id: "pa-select-clients",
									isVisible: true,
								},
							],
						},
						{
							sortingIndex: 4,
							fields: [
								{
									sortingIndex: 0,
									id: "pa-select-invoices",
									isVisible: true,
								},
							],
						},
						{
							sortingIndex: 5,
							fields: [
								{
									sortingIndex: 0,
									id: "pa-currency",
									isVisible: true,
								},
								{
									sortingIndex: 1,
									id: "pa-received-amount",
									isVisible: true,
								},
							],
						},
						{
							sortingIndex: 6,
							fields: [
								{
									sortingIndex: 0,
									id: "pa-received-date",
									isVisible: true,
								},
								{
									sortingIndex: 1,
									id: "pa-payment-state",
									isVisible: true,
								},
							],
						},
						{
							sortingIndex: 7,
							fields: [
								{
									sortingIndex: 0,
									id: "pa-payment-purpose",
									isVisible: true,
								},
							],
						},
						{
							sortingIndex: 8,
							fields: [
								{
									sortingIndex: 0,
									id: "pa-remarks",
									isVisible: true,
								},
							],
						},
					],
				},
			],
		},
	},
	file: {
		isFormModelOpen: false,
		isListModelOpen: false,
		list: [],
		form: {
			sections: fileFormSections,
		},
	},
};

export const productListState: DataDisplayList[] = [
	{
		fieldName: "productId",
		fieldLabel: "Product ID",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 0,
		isCustomField: false,
		isPinned: false,
	},
	{
		fieldName: "name",
		fieldLabel: "Name",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 1,
		isCustomField: false,
		isPinned: false,
	},
	{
		fieldName: "listedPrice",
		fieldLabel: "Price",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 2,
		isCustomField: false,
		isPinned: false,
	},
	{
		fieldName: "category",
		fieldLabel: "Category",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 3,
		isCustomField: false,
		isPinned: false,
	},
	{
		fieldName: "type",
		fieldLabel: "Type",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 4,
		isCustomField: false,
		isPinned: false,
	},
	{
		fieldName: "optionGroups",
		fieldLabel: "Option Groups",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 5,
		isCustomField: false,
		isPinned: false,
	},
	{
		fieldName: "options",
		fieldLabel: "Options",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 6,
		isCustomField: false,
		isPinned: false,
	},
];

export const accountTransactionListState: DataDisplayList[] = [
	{
		fieldName: "transactionNumber",
		fieldLabel: "Transaction #",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 0,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
		width: 200,
	},
	{
		fieldName: "createdBy",
		fieldLabel: "Created By",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 1,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
	},
	{
		fieldName: "createdAt",
		fieldLabel: "Created At",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 2,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
	},
	{
		fieldName: "accountName",
		fieldLabel: "Account Name",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 3,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
	},
	{
		fieldName: "accountId",
		fieldLabel: "Account ID",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 4,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
	},
	{
		fieldName: "agentName",
		fieldLabel: "Agent Name",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 5,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
	},
	{
		fieldName: "clientName",
		fieldLabel: "Client Name",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 6,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
	},
	{
		fieldName: "orderNumber",
		fieldLabel: "Order Number",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 7,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
	},
	{
		fieldName: "paymentType",
		fieldLabel: "Payment Type",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 8,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
	},
	{
		fieldName: "paymentAmount",
		fieldLabel: "Payment Amount",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 9,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
	},
	{
		fieldName: "bankPaymentReceivedDate",
		fieldLabel: "Bank Payment Received Date",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 10,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
		width: 280,
	},
	{
		fieldName: "paymentAttachment",
		fieldLabel: "Payment Attachment",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 11,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
	},
	{
		fieldName: "paymentAttachmentAmount",
		fieldLabel: "Payment Attachment Amount",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 12,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
	},
	{
		fieldName: "paymentAttachmentReferenceNumber",
		fieldLabel: "Payment Attachment Number",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 13,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
		width: 280,
	},
	{
		fieldName: "paymentAttachmentStage",
		fieldLabel: "Payment Attachment Stage",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 14,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
	},
	{
		fieldName: "comments",
		fieldLabel: "Comments",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 15,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
	},
	{
		fieldName: "paymentReferenceNumber",
		fieldLabel: "Payment Reference Number",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 16,
		isCustomField: false,
		isPinned: false,
		isVisible: true,
	},
];

export interface DataDisplayContext extends DataDisplayState {
	handleAgentFormModelOpen: () => void;
	handleAgentFormModelClose: () => void;
	handleAgentListModelOpen: () => void;
	handleAgentListModelClose: () => void;
	handleChangeAgentListSettings: (
		change: any[],
		templateType?: DISPLAY_PREFERENCE_TYPE | null,
	) => Promise<void>;
	handleChangeAgentFormSettings: (
		type: DATA_DISPLAY_CHANGE_TYPE,
		change: any[],
	) => Promise<void>;
	handleClientFormModelOpen: () => void;
	handleClientFormModelClose: () => void;
	handleClientListModelOpen: () => void;
	handleClientListModelClose: () => void;
	handleChangeClientListSettings: (
		change: any[],
		templateType?: DISPLAY_PREFERENCE_TYPE | null,
	) => Promise<void>;
	handleChangeClientFormSettings: (
		type: DATA_DISPLAY_CHANGE_TYPE,
		change: any[],
	) => Promise<void>;
	handleProductListModelOpen: () => void;
	handleProductListModelClose: () => void;
	handleChangeProductListSettings: (change: any[]) => void;
	handleChangeServiceAgentListSettings: (change: any[]) => void;
	handleChangeServiceClientListSettings: (change: any[]) => void;
	handleSupplierFormModelOpen: () => void;
	handleSupplierFormModelClose: () => void;
	handleSupplierListModelOpen: () => void;
	handleSupplierListModelClose: () => void;
	handleChangeSupplierListSettings: (change: any[]) => void;
	handleChangeSupplierFormSettings: (
		type: DATA_DISPLAY_CHANGE_TYPE,
		change: any[],
	) => void;
	handleReceiverFormModelOpen: () => void;
	handleReceiverFormModelClose: () => void;
	handleReceiverListModelOpen: () => void;
	handleReceiverListModelClose: () => void;
	handleChangeReceiverListSettings: (change: any[]) => void;
	handleChangeReceiverFormSettings: (
		type: DATA_DISPLAY_CHANGE_TYPE,
		change: any[],
	) => void;
	handleAccountTransactionFormModelOpen: () => void;
	handleAccountTransactionFormModelClose: () => void;
	handleAccountTransactionListModelOpen: () => void;
	handleAccountTransactionListModelClose: () => void;
	handleChangeAccountTransactionListSettings: (change: any[]) => void;
	handleChangeAccountTransactionFormSettings: (
		type: DATA_DISPLAY_CHANGE_TYPE,
		change: any[],
	) => void;
	handlePaymentAttachmentFormModelOpen: () => void;
	handlePaymentAttachmentFormModelClose: () => void;
	handlePaymentAttachmentListModelOpen: () => void;
	handlePaymentAttachmentListModelClose: () => void;
	handleChangePaymentAttachmentListSettings: (change: any[]) => void;
	handleChangePaymentAttachmentFormSettings: (
		type: DATA_DISPLAY_CHANGE_TYPE,
		change: any[],
	) => void;
	handleFileFormModelOpen: () => void;
	handleFileFormModelClose: () => void;
	handleChangeFileFormSettings: (
		type: DATA_DISPLAY_CHANGE_TYPE,
		change: any[],
	) => Promise<void>;
}

export const DataDisplayContext = createContext<DataDisplayContext>({
	...initialState,
	handleAgentFormModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleAgentFormModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleAgentListModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleAgentListModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleChangeAgentListSettings: function (
		_change: any[],
		_templateType?: DISPLAY_PREFERENCE_TYPE | null,
	): Promise<void> {
		throw new Error("Function not implemented.");
	},
	handleChangeAgentFormSettings: function (
		_type: DATA_DISPLAY_CHANGE_TYPE,
		_change: any[],
	): Promise<void> {
		throw new Error("Function not implemented.");
	},
	handleClientFormModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleClientFormModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleClientListModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleClientListModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleChangeClientListSettings: function (
		_change: any[],
		_templateType?: DISPLAY_PREFERENCE_TYPE | null,
	): Promise<void> {
		throw new Error("Function not implemented.");
	},
	handleChangeClientFormSettings: function (
		_type: DATA_DISPLAY_CHANGE_TYPE,
		_change: any[],
	): Promise<void> {
		throw new Error("Function not implemented.");
	},
	handleProductListModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleProductListModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleChangeProductListSettings: function (_change: any[]): void {
		throw new Error("Function not implemented.");
	},
	handleChangeServiceAgentListSettings: function (_change: any[]): void {
		throw new Error("Function not implemented.");
	},
	handleChangeServiceClientListSettings: function (_change: any[]): void {
		throw new Error("Function not implemented.");
	},
	handleSupplierFormModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleSupplierFormModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleSupplierListModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleSupplierListModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleChangeSupplierListSettings: function (_change: any[]): void {
		throw new Error("Function not implemented.");
	},
	handleChangeSupplierFormSettings: function (
		_type: DATA_DISPLAY_CHANGE_TYPE,
		_change: any[],
	): void {
		throw new Error("Function not implemented.");
	},
	handleReceiverFormModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleReceiverFormModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleReceiverListModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleReceiverListModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleChangeReceiverListSettings: function (_change: any[]): void {
		throw new Error("Function not implemented.");
	},
	handleChangeReceiverFormSettings: function (
		_type: DATA_DISPLAY_CHANGE_TYPE,
		_change: any[],
	): void {
		throw new Error("Function not implemented.");
	},
	handleAccountTransactionFormModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleAccountTransactionFormModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleAccountTransactionListModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleAccountTransactionListModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleChangeAccountTransactionListSettings: function (_change: any[]): void {
		throw new Error("Function not implemented.");
	},
	handleChangeAccountTransactionFormSettings: function (
		_type: DATA_DISPLAY_CHANGE_TYPE,
		_change: any[],
	): void {
		throw new Error("Function not implemented.");
	},
	handlePaymentAttachmentFormModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handlePaymentAttachmentFormModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handlePaymentAttachmentListModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handlePaymentAttachmentListModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleChangePaymentAttachmentListSettings: function (_change: any[]): void {
		throw new Error("Function not implemented.");
	},
	handleChangePaymentAttachmentFormSettings: function (
		_type: DATA_DISPLAY_CHANGE_TYPE,
		_change: any[],
	): void {
		throw new Error("Function not implemented.");
	},
	handleFileFormModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleFileFormModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleChangeFileFormSettings: function (
		_type: DATA_DISPLAY_CHANGE_TYPE,
		_change: any[],
	): Promise<void> {
		throw new Error("Function not implemented.");
	},
});
